/**
 * EditFinesDialog — iter206.
 *
 * Admin dialog to tweak (or fully waive) individual fines AFTER they were
 * auto-issued (late_cancel, late_10, late_15, no_show) or manually issued.
 * Each row shows the current amount + a pencil → opens an inline editor
 * with [new amount] + [optional reason]. Submit hits
 * POST /api/sessions/{id}/fines/edit which atomically rebases the
 * member's coin_balance + the group fund_balance + writes an audit trail.
 *
 * Setting new_amount=0 fully waives the fine (marks reversed + writes
 * paired fine_reversal entry, same as the /fund-transactions/{id}/reverse
 * endpoint).
 *
 * Triggered from the SessionCard kebab menu ("Edit fines").
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, Save, Edit3, CheckCircle2, X, ShieldAlert, AlertTriangle, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';

const FINE_TYPE_LABEL = {
  late_cancel: 'Late cancel',
  late_10: 'Late ≤10m',
  late_15: 'Late ≤15m',
  no_show: 'No-show',
  custom: 'Custom',
};

export default function EditFinesDialog({ open, sessionId, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);  // txn_id being edited
  const [draftAmount, setDraftAmount] = useState('');
  const [draftReason, setDraftReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/sessions/${sessionId}/fines/attendees`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load fines');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (open) load();
    else { setData(null); setEditing(null); }
  }, [open, load]);

  const onStartEdit = (row) => {
    setEditing(row.txn_id);
    setDraftAmount(String(row.amount));
    setDraftReason('');
  };

  const onCancelEdit = () => {
    setEditing(null);
    setDraftAmount('');
    setDraftReason('');
  };

  const submitEdit = useCallback(async (overrideAmount, txnId) => {
    const amt = overrideAmount !== undefined ? overrideAmount : parseFloat(draftAmount);
    const fineTxnId = txnId || editing;
    if (Number.isNaN(amt) || amt < 0) {
      toast.error('Enter a valid amount (₹0 to waive)');
      return;
    }
    if (!fineTxnId) {
      toast.error('Could not identify the fine — please reopen this dialog');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/fines/edit`, {
        fine_txn_id: fineTxnId,
        new_amount: amt,
        reason: draftReason.trim() || undefined,
      });
      toast.success(r.data?.message || 'Updated');
      await load();
      setEditing(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Edit failed');
    } finally {
      setSaving(false);
    }
  }, [draftAmount, draftReason, editing, sessionId, load, onChange]);

  const onWaiveAll = useCallback(async (row) => {
    if (!window.confirm(`Waive ₹${Math.round(row.amount)} fine for ${row.user_name}? They'll be refunded.`)) return;
    setEditing(row.txn_id);
    setDraftReason('');
    submitEdit(0, row.txn_id);
  }, [submitEdit]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose?.()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="edit-fines-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-600" />
            Edit session fines
          </DialogTitle>
          <DialogDescription>
            {data?.session_title}
            {data?.session_date ? ` · ${data.session_date}` : ''}
            {' · '}Total {data ? `₹${Math.round(data.fines_total).toLocaleString('en-IN')}` : '…'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        )}
        {error && (
          <div className="rounded-xl border-2 border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        {data && !loading && data.fines.length === 0 && (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No fines issued for this session.
          </div>
        )}
        {data && !loading && data.fines.length > 0 && (
          <div className="space-y-1.5 py-2" data-testid="edit-fines-list">
            {data.fines.map((row) => {
              const isEditing = editing === row.txn_id;
              return (
                <div
                  key={row.txn_id}
                  className={`rounded-xl border-2 ${row.reversed ? 'border-border opacity-50' : 'border-amber-500/30 bg-amber-500/5'} px-3 py-2`}
                  data-testid={`edit-fine-row-${row.txn_id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 text-xs font-bold shrink-0">
                      {(row.user_name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{row.user_name}</p>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        <span className="inline-block px-1.5 py-0.5 rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-300 font-semibold text-xs uppercase tracking-wider mr-1">
                          {FINE_TYPE_LABEL[row.fine_type] || row.fine_type}
                        </span>
                        {row.reversed ? (
                          <span className="text-red-600 font-semibold">waived</span>
                        ) : row.edited ? (
                          <>edited from ₹{Math.round(row.edited_from_amount || 0)}{row.edited_reason ? ` · ${row.edited_reason}` : ''}</>
                        ) : null}
                      </p>
                    </div>
                    {!isEditing && (
                      <>
                        <span className="text-sm font-bold tabular-nums shrink-0">
                          ₹{Math.round(row.amount).toLocaleString('en-IN')}
                        </span>
                        {!row.reversed && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onStartEdit(row)}
                              data-testid={`edit-fine-edit-${row.txn_id}`}
                              className="h-7 px-2 gap-1"
                            >
                              <Edit3 className="w-3.5 h-3.5" /> Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => onWaiveAll(row)}
                              data-testid={`edit-fine-waive-${row.txn_id}`}
                              className="h-7 w-7 p-0 text-red-600 hover:bg-red-500/10"
                              title="Waive fully"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  {isEditing && (
                    <div className="mt-2 pt-2 border-t border-border space-y-2" data-testid={`edit-fine-editor-${row.txn_id}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-muted-foreground">₹</span>
                        <Input
                          type="number"
                          inputMode="decimal"
                          min="0"
                          value={draftAmount}
                          onChange={(e) => setDraftAmount(e.target.value)}
                          className="h-9 text-base flex-1"
                          autoFocus
                          data-testid={`edit-fine-amount-${row.txn_id}`}
                        />
                      </div>
                      <Input
                        placeholder="Reason (optional, e.g. 'arrived on time')"
                        value={draftReason}
                        onChange={(e) => setDraftReason(e.target.value)}
                        className="h-9 text-sm"
                        data-testid={`edit-fine-reason-${row.txn_id}`}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={onCancelEdit} disabled={saving} data-testid={`edit-fine-cancel-${row.txn_id}`} className="h-8 gap-1">
                          <X className="w-3.5 h-3.5" /> Cancel
                        </Button>
                        <Button size="sm" onClick={() => submitEdit()} disabled={saving} data-testid={`edit-fine-save-${row.txn_id}`} className="h-8 gap-1">
                          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          Save
                        </Button>
                      </div>
                      {parseFloat(draftAmount) !== row.amount && !Number.isNaN(parseFloat(draftAmount)) && (
                        <p className="text-xs text-muted-foreground italic flex items-start gap-1">
                          {parseFloat(draftAmount) === 0
                            ? <><CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5" /> Fine fully waived — ₹{Math.round(row.amount)} refunded to {row.user_name}.</>
                            : parseFloat(draftAmount) < row.amount
                              ? <><CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5" /> Refund of ₹{Math.round(row.amount - parseFloat(draftAmount))} will be credited to {row.user_name}.</>
                              : <><AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5" /> Additional ₹{Math.round(parseFloat(draftAmount) - row.amount)} will be charged to {row.user_name}.</>}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="edit-fines-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
